/**
 * Environment + release-version resolution — the single source of truth the shell
 * chrome reads to paint the dev/stage indicator and the version string.
 *
 * Both values are baked in at build time by the deploy workflow's `spa` job:
 *   VITE_APP_ENV      dev | stage | prod   (from the GH `ENV_NAME` variable)
 *   VITE_APP_VERSION  a `rel-*` tag for stage/prod, else `dev-<short-sha>` on trunk
 * They're absent for local `pnpm dev`, so both fall back to a `local` sentinel.
 *
 * Prod safety: the env strip/pill render ONLY for `dev`/`stage`. Any other value
 * (incl. an unset one → `local`) shows nothing, so a build glitch can't badge prod.
 */

export type AppEnv = 'dev' | 'stage' | 'prod' | 'local';

function readEnv(): AppEnv {
  const raw = import.meta.env.VITE_APP_ENV;
  return raw === 'dev' || raw === 'stage' || raw === 'prod' ? raw : 'local';
}

export const APP_ENV: AppEnv = readEnv();

/** Raw version as built (`rel-2026.07.17`, `dev-abc1234`, or `local`). */
export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION || 'local';

/** Only dev/stage get the visible environment cue. */
export const isNonProd = APP_ENV === 'dev' || APP_ENV === 'stage';

/**
 * Whether the MFA enrollment HARD WALL is enforced in this build (Stream 4).
 * Baked in by the deploy workflow via `VITE_MFA_ENROLLMENT_ENABLED`; **absent =
 * off**. This is controlled enablement, mirroring appcore's per-workspace floor
 * (docs/28 §6 / P1c): the wall is a UX layer over that floor, so turning it on
 * before the floor is enabled per workspace (and before the e2e fixtures enroll a
 * factor) would just wall every existing session/test out for nothing. Enabled
 * deliberately alongside flooring the workspace, NOT automatically when the SPA
 * ships. Read as a function so tests can stub it per case. The step-up interceptor
 * is NOT gated by this — it is inert until a real 403 arrives, which only the floor
 * produces.
 */
export function mfaEnrollmentEnabled(): boolean {
  return import.meta.env.VITE_MFA_ENROLLMENT_ENABLED === 'true';
}

/**
 * Whether social login (Google, Wave 1) is wired for this build's environment.
 * Baked in by the deploy workflow via `VITE_SOCIAL_LOGIN_ENABLED`; **absent = off**.
 * Controlled enablement mirroring `mfaEnrollmentEnabled()`: the "Add a Google login"
 * link ceremony (the Logins section of Settings -> Security) only works when the hub
 * pool actually has the Google IdP configured for this env (dev today; stage/prod once
 * the OAuth clients + custom auth domains land). Turning the SPA affordance on before
 * the IdP exists would surface a button whose hosted-UI federation the pool rejects.
 * Read as a function so tests can stub it per case, exactly like the MFA wall above.
 *
 * The generic "Sign in" button (Login.tsx) is deliberately NOT gated by this: it hands
 * off to the Cognito Hosted UI, which only renders providers the pool has configured,
 * so it is correct in every env regardless. Only the in-app link ceremony needs gating.
 */
export function socialLoginEnabled(): boolean {
  return import.meta.env.VITE_SOCIAL_LOGIN_ENABLED === 'true';
}

/**
 * Whether the People → Advisors "What you share" control renders in this build
 * (steward-app#739). Baked in by the deploy workflow via `VITE_SHARING_AREAS_ENABLED`;
 * **absent = off**. Read as a function so tests can stub it per case, exactly like the two
 * above.
 *
 * ⚠️ **Where it is set is a dated reading, not a property of this file.** As of 2026-09-14
 * the `SHARING_AREAS_ENABLED` GitHub Environment variable is `true` in **dev** (set
 * 2026-08-27) and unset in **stage** and **prod**. Taken from
 * `gh api repos/crossmarc/steward-app/environments/{env}/variables`; re-read it rather than
 * trusting this line, and note that the value here is baked in at BUILD time, so the flag a
 * running bundle carries is the one its deploy was built with.
 *
 * ## Why this gate exists, and what changed under it
 *
 * It was an HONESTY gate. While the pillar capabilities were granted but not armed
 * (phase 2a), `steward-advisor`'s `inst:read` still covered the whole workspace, so an area
 * shown as off withheld nothing — a screen saying "Contacts: not shared with RIPL Wealth"
 * beside a firm that could read every contact was a FALSE statement rather than an inert
 * control, and the founder's 2026-08-27 default (contacts off) meant a family would see that
 * sentence without ever touching a switch.
 *
 * ⚠️ **That is no longer the situation.** Phase 2b armed `accessControl.readCapability` on
 * every type the pillars governed then (`packages/steward-types/src/index.ts`), so the claim
 * the control makes is TRUE. Where it stays off, that is now a different and much weaker
 * reason: turning it on is a deliberate per-environment operator step (set the
 * `SHARING_AREAS_ENABLED` GitHub Environment variable to `true` and redeploy), and it is the
 * founder's call.
 *
 * ⚠️ **What keeps the claim true as NEW pillars arrive is not this flag and not phase 2b.**
 * A pillar is granted one deploy and armed the next (`PILLAR_GRANTED_NOT_YET_ARMED`), so the
 * table can legitimately hold one that withholds nothing. The control renders
 * `LIVE_SHARING_PILLARS`, which excludes exactly those, and the façade refuses them on the
 * wire. That filter is the honesty mechanism now; this flag is only a visibility switch.
 * Do not reason from "the flag is on, therefore everything on screen is armed" — and equally,
 * do not reason from "nothing is in flight" that the filter can be removed. (Nothing is in
 * flight on 2026-09-14: `people` was armed by steward-app#1260 phase 2, so the screen offers
 * all five areas including Family. That is a dated reading of a constant, not a property.)
 *
 * The residual risk has therefore INVERTED, and that is worth knowing before leaving this
 * off for long: with the capabilities armed and this flag off, a new connection genuinely
 * withholds Contacts and the family has no surface that says so, nor any way to turn it back
 * on. That is an under-claim rather than a false claim, so it is the safer of the two — but
 * it is the argument for flipping this in the same maintenance window as the arming deploy
 * rather than leaving it for later.
 */
export function sharingAreasEnabled(): boolean {
  return import.meta.env.VITE_SHARING_AREAS_ENABLED === 'true';
}

/**
 * Whether Ask Steward is offered in an advisory firm's OWN workspace (steward-app#1815).
 * Baked in by the deploy workflow via `VITE_FIRM_ASSISTANT_ENABLED`, which `deploy.yml`
 * sets to `true` for every environment (#2525); **absent = off**, which keeps steward-app#1541's behaviour
 * (no Ask control, no docked panel, `/ask` redirects). Read as a function so tests can stub
 * it per case, like the flags above.
 *
 * This is a visibility switch, not the gate. The façade decides the workspace kind from the
 * active workspace's packs and refuses a firm-kind question with 403
 * `FIRM_ASSISTANT_DISABLED` unless its own `FIRM_ASSISTANT_ENABLED` is set (on in every
 * environment, `deploy/lib/api-stack.ts`), so a bundle built with this on against an API without it
 * shows a panel whose questions are refused, never a firm workspace that mounts
 * `steward-advisor-firm` answered by the household path. (A firm workspace provisioned
 * without that pack reads as a household to the façade and gets the household assistant,
 * as it did before #1815; see `resolveAssistantWorkspaceKind`.)
 */
export function firmAssistantEnabled(): boolean {
  return import.meta.env.VITE_FIRM_ASSISTANT_ENABLED === 'true';
}

/**
 * The Cloudflare Turnstile SITE key for the public intake page, or `''` when this build
 * has none (steward-app#623, B5, docs/25 §11).
 *
 * Not a boolean like the four flags above, because the value itself is the configuration:
 * a site key is PUBLIC by design (it identifies the widget to Cloudflare and authorizes
 * nothing), so it belongs in the bundle. The matching SECRET key never comes near the SPA
 * — it is an SSM SecureString the façade reads at runtime.
 *
 * ⚠️ **Empty is the shipped state and the safe one.** No key → `TurnstileWidget` renders
 * nothing, no third-party script is loaded, no header is sent, and the intake wizard is
 * byte-for-byte the page it was. The server half is independently inert (it has no secret),
 * so neither half can turn the control on alone.
 *
 * ⚠️ **Set this BEFORE the server's secret, never after.** The two switches are separate
 * on purpose, and the order matters: a build carrying a site key against a server with no
 * secret sends a token nobody checks, which is harmless. The reverse — a server holding a
 * secret while the deployed bundle sends no token — means every real submission is refused,
 * and because a refusal is answered with the same 202 a stored submission gets, every
 * family would read a thank-you screen for disclosure that was binned.
 *
 * Wired in `.github/workflows/deploy.yml` from the `TURNSTILE_SITE_KEY` GitHub environment
 * variable — a flag that is only ever read from a local `.env` is dead code with a switch
 * drawn on it (the lesson `VITE_ADVISOR_EXCHANGE_PREVIEW_ENABLED`, steward-app#733, cost:
 * off in every deployed environment for its entire life, dark by accident rather than by
 * design, unflippable without a code change). Read as a function so tests can stub it per
 * case.
 */
export function turnstileSiteKey(): string {
  return import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
}

/**
 * The one production API base URL this SPA is ever built against (`deploy/bin/index.ts`,
 * `API_CUSTOM_DOMAIN.prod`). Production code does not branch on it: the dev and stage "test copy"
 * notices (steward-app#1009) were removed by crossmarc/steward-app#2430. Tests import it to pin the API a build posts to.
 */
export const PROD_API_BASE_URL = 'https://api.steward.crossmarc.com';

/** Trim the `rel-` promotion prefix for display (`rel-2026.07.17` → `2026.07.17`).
 *  Leaves `dev-<sha>` and `local` untouched. Pure — the version-string contract. */
export function trimRelPrefix(version: string): string {
  return version.replace(/^rel-/, '');
}

/** Display form of the running build's version. */
export function versionLabel(): string {
  return trimRelPrefix(APP_VERSION);
}

/** Per-env chrome metadata. Colors sit off the bronze brand accent on purpose, so the
 *  cue never reads as ordinary prod chrome. `null` for prod/local (no indicator). */
export const ENV_META: Record<'dev' | 'stage', { label: string; strip: string; pill: string }> = {
  // amber
  dev: {
    label: 'Dev',
    strip: 'bg-[#D97706]',
    pill: 'bg-[#FEF3C7] text-[#B45309]',
  },
  // violet
  stage: {
    label: 'Stage',
    strip: 'bg-[#7C3AED]',
    pill: 'bg-[#EDE9FE] text-[#6D28D9]',
  },
};

/** The metadata for the current env, or null when there's nothing to show. */
export function currentEnvMeta() {
  return isNonProd ? ENV_META[APP_ENV as 'dev' | 'stage'] : null;
}

/**
 * Hidden/on-demand surfaces, stamped once at boot: `<meta>` tags (view-source),
 * `window.__APP_VERSION__` (console poke), and a one-line console banner. All carry
 * the full untrimmed version so support can read the exact build.
 *
 * Also stamps `meta[name="mfa-enrollment-enabled"]` with the build's effective wall
 * state. This exists so post-deploy smoke can assert the SERVED artifact's posture
 * directly (mirroring how `app-version` lets smoke verify the served bundle, not
 * just the workflow's build-time inputs) — unlike `VITE_APP_VERSION`,
 * `VITE_MFA_ENROLLMENT_ENABLED` collapses to a bare inlined boolean at minification
 * and isn't reliably greppable in the built JS, so it needs an explicit, stable
 * carrier. Called unconditionally at boot (main.tsx), before routing/auth resolve,
 * so it is observable on every page — including /login — regardless of sign-in
 * state. See e2e/mfa-posture.spec.ts. (No `window.__MFA_...__` global — the meta
 * tag alone satisfies the smoke check, so there's nothing else to keep in sync.)
 */
export function stampVersionMetadata(): void {
  const mfaEnabled = mfaEnrollmentEnabled();
  if (typeof document !== 'undefined') {
    // Idempotent: HMR re-executing this module (dev) must not append twice.
    if (document.head.querySelector('meta[name="app-version"]')) return;
    const meta = (name: string, content: string) => {
      const el = document.createElement('meta');
      el.name = name;
      el.content = content;
      document.head.appendChild(el);
    };
    meta('app-version', APP_VERSION);
    meta('app-env', APP_ENV);
    meta('mfa-enrollment-enabled', String(mfaEnabled));
  }
  if (typeof window !== 'undefined') {
    window.__APP_VERSION__ = APP_VERSION;
  }
  // eslint-disable-next-line no-console
  console.info(`Steward ${versionLabel()} · ${APP_ENV}`);
}
